import React, { useState, useEffect, useRef } from 'react';
import './../styles/components/_contact-form.scss';
import { findBestAnswer } from '../utils/useQAMatch';

const getLinkIcon = (url) => {
  if (url.startsWith('https://maps.app.goo.gl')) return 'fas fa-map-marker-alt';
  if (url.startsWith('https://wa.link')) return 'fab fa-whatsapp';
  if (url.startsWith('mailto:')) return 'fas fa-envelope';
  if (url.startsWith('tel:')) return 'fas fa-phone';
  return 'fas fa-external-link-alt';
};

const getLinkText = (url) => {
  if (url.startsWith('https://maps.app.goo.gl')) return 'Get Directions';
  if (url.startsWith('https://wa.link')) return 'Chat on WhatsApp';
  if (url.startsWith('mailto:')) return url.replace('mailto:', '');
  if (url.startsWith('tel:')) return url.replace('tel:', '');
  if (url.includes('vartohorganic.com')) return 'Visit Vartoh Organic Website';
  if (url.includes('amazon.in')) return 'Visit Amazon Store';
  if (url.includes('flipkart.com')) return 'Visit Flipkart Store';
  return 'Visit Website';
};

const processUrl = (url) => {
  try {
    const cleanUrl = url.substring(1);
    if (cleanUrl.includes('%') || cleanUrl.includes('xn--')) return decodeURIComponent(cleanUrl);
    if (cleanUrl.includes('vartohorganic.com')) return 'https://vartohorganic.com';
    return cleanUrl;
  } catch (error) {
    console.error('Error processing URL:', error);
    return url.substring(1);
  }
};

const Chatbot = () => {
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: 'Hi! Welcome to Mevedir. I can help you with information about our products, services, locations, and more. What would you like to know?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [lastContext, setLastContext] = useState(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const checkScrollPosition = () => {
      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollButton(!isNearBottom);
      }
    };

    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', checkScrollPosition);
      scrollToBottom();
    }

    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [messages]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = {
      sender: 'user',
      text: input.trim(),
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const botReply = await getBotResponse(input.trim());
      const botText = typeof botReply === 'string' ? botReply : botReply.answer;

      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: botText,
          timestamp: new Date()
        }
      ]);

      if (typeof botReply !== 'string' && botReply.followUps) {
        setLastContext(botReply);
      } else {
        setLastContext(null);
      }
    } catch (error) {
      console.error('Error getting bot response:', error);
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: "I'm having trouble understanding you right now. Please try again later or contact our support team.",
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const getBotResponse = async (userInput) => {
    const normalizedInput = userInput.toLowerCase().trim();
  
    const greetings = {
      hi: 'Hi',
      hello: 'Hello',
      hey: 'Hey',
      'good morning': 'Good morning',
      'good afternoon': 'Good afternoon',
      'good evening': 'Good evening',
      'whats up': "What's up",
      namaste: 'Namaste',
      hola: 'Hola',
      bonjour: 'Bonjour'
    };
  
    if (greetings[normalizedInput]) {
      return `${greetings[normalizedInput]}! How are you doing? What would you like to know about Mevedir?`;
    }
  
    // ✅ FOLLOW-UP CHECK with fallback
    if (lastContext?.followUps) {
      for (const key in lastContext.followUps) {
        if (normalizedInput.includes(key.toLowerCase())) {
          const followUpText = lastContext.followUps[key];
          setLastContext(null);
          return { answer: followUpText };
        }
      }
  
      // ❌ Fallback if follow-up failed
      return {
        answer: "🤖 I didn’t quite get that. Would you like the contact info or ask something else?",
        followUps: {
          "yes": "📞 You can call us at @tel:+919002333165 or email @mailto:info@mevedir.com.",
          "contact": "📧 Drop us a mail at @mailto:info@mevedir.com.",
          "human": "Sure! Our team is available at @tel:+919002333165.",
          "phone": "📞 You can call us at @tel:+919002333165.",
          "email": "📧 Drop us a mail at @mailto:info@mevedir.com.",
          "whatsapp": "💬 Chat with us @https://wa.link/hm5f3z"
        }
      };
    }
  
    return await findBestAnswer(userInput);
  };
  
  

    return (
        <div className="chatbot-container">
            <div className="chatbot-messages" ref={messagesContainerRef}>
                {messages.map((msg, index) => {
                    const words = msg.text.split(' ');
                    const links = words.filter(word => word.startsWith('@'));
                    const hasMultipleLinks = links.length > 1;
                    
                    return (
                        <div 
                            key={index} 
                            className={`${msg.sender}-message`}
                            style={{
                                animationDelay: `${index * 0.1}s`
                            }}
                        >
                            {hasMultipleLinks ? (
                                <>
                                    {words.map((word, wordIndex) => {
                                        if (!word.startsWith('@')) {
                                            return word + ' ';
                                        }
                                        return null;
                                    })}
                                    <div className="contact-list">
                                        {links.map((link, linkIndex) => {
                                            const url = processUrl(link);
                                            return (
                                                <a 
                                                    key={linkIndex}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="message-link"
                                                >
                                                    <i className={getLinkIcon(url)}></i>
                                                    {getLinkText(url)}
                                                </a>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="message-content">
                                    {msg.text.split('\n').map((line, lineIndex) => (
                                        <React.Fragment key={lineIndex}>
                                            {line.split(' ').map((word, wordIndex) => {
                                    if (word.startsWith('@')) {
                                                    const url = processUrl(word);
                                        return (
                                            <a 
                                                key={wordIndex}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="message-link"
                                            >
                                                <i className={getLinkIcon(url)}></i>
                                                {getLinkText(url)}
                                            </a>
                                        );
                                    }
                                    return word + ' ';
                                            })}
                                            {lineIndex < msg.text.split('\n').length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                {isTyping && (
                    <div className="bot-message typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                )}
                {showScrollButton && (
                    <button 
                        className="scroll-to-bottom-button"
                        onClick={scrollToBottom}
                        aria-label="Scroll to bottom"
                    >
                        <i className="fas fa-arrow-down"></i>
                    </button>
                )}
            </div>
            <div className="chatbot-input">
                <input 
                    ref={inputRef}
                    type="text" 
                    value={input} 
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..." 
                    disabled={isTyping}
                />
                <button 
                    onClick={sendMessage}
                    disabled={!input.trim() || isTyping}
                >
                    Send
                </button>
            </div>
        </div>
    );
};

const Contact = () => {
  return (
    <div className="contact-page">
      <div className="contact-form-container">
        <h2>Chat with Us</h2>
        <p>Ask us anything about our products and services!</p>
        <Chatbot />
      </div>
    </div>
  );
};

export default Contact;
